import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteClear } from '../../../constants/SwalAlerts';
import { GetProductsAndStore } from '../../../pages/auth/GetProductsAndStore';

export default function DepartmentSelect() {
  const apiurl = localStorage.getItem('apiurl');
  const dbTokenData = localStorage.getItem('dbTokenData');

  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('Department');
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const storedDivisionData = localStorage.getItem('divisionData');
    if (storedDivisionData) {
      try {
        const dropdownOptions = JSON.parse(storedDivisionData);
        setDepartments(dropdownOptions);
        const savedDept = localStorage.getItem('selectedDepartment');
        if (savedDept) {
          setSelectedDepartment(savedDept);
        }
      } catch (error) {
        toast.error('Failed to parse divisionData');
        console.error('Parse error:', error);
      }
    }
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDepartmentSelect = async (divcode, divname) => {
    if (selectedDepartment === divname) {
      handleClose();
      return;
    }
    const isConfirmed = await ConfirmDeleteClear('Are you sure', 'You want to change department?');
    if (!isConfirmed) {
      handleClose();
      return;
    }
    setSelectedDepartment(divname);
    localStorage.setItem('selectedDepartment', divname);
    localStorage.setItem('selectedDivisionCode', divcode);
    handleClose();
    localStorage.removeItem('productsData');
    const apiResult = await GetProductsAndStore(divcode, dbTokenData, apiurl);
    if (apiResult) {
      window.location.reload();
    } else {
      toast.error('Error updating products');
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main',
            transform: 'translateY(-1px)',
            boxShadow: 1
          }
        }}
      >
        <Building2 size={18} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {selectedDepartment}
        </Typography>
        <ChevronDown
          size={16}
          style={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        sx={{
          mt: 1
        }}
        PaperProps={{
          sx: {
            width: 280,
            maxHeight: 400,
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            SWITCH DEPARTMENT
          </Typography>
        </Box>
        <List sx={{ p: 1 }}>
          {departments.length === 0 ? (
            <ListItem disablePadding>
              <ListItemButton disabled sx={{ borderRadius: 1 }}>
                <ListItemText
                  primary="No departments available"
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            departments.map((dept) => (
              <ListItem key={dept.id} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleDepartmentSelect(dept.code, dept.name)}
                  selected={selectedDepartment === dept.name}
                  sx={{
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: 'primary.lighter',
                      '&:hover': {
                        bgcolor: 'primary.light'
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    <Building2 size={16} />
                    <ListItemText
                      primary={dept.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: selectedDepartment === dept.name ? 600 : 400
                      }}
                    />
                    {selectedDepartment === dept.name && <Check size={16} color="#1976d2" />}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Popover>
    </>
  );
}
